import { create } from 'zustand';
import { WhiteboardState, Stroke, Tool, Point, PenSettings, Recording, RecordingSession, RecordingEvent, OpticalAnswer } from '../types';
import { RecordingService } from '../lib/firebase/recordingService';
import { useUserStore } from './useUserStore';
import { useTenantStore } from './useTenantStore';
import { QuestionData, DevicePreferences } from '../types/questionData';
import { TestMetadata } from '../types/testMetadata';
import { devicePreferencesService } from '../lib/firebase/devicePreferencesService';
import { QuestionDataLocalStorage } from '../lib/localStorage/questionDataLocalStorage';
import type { Canvas as FabricCanvas } from 'fabric';
import { normalizeRecordingEvent } from '../utils/normalizedCoordinates';
import fixWebmDuration from 'webm-duration-fix';
// import { useParams } from 'react-router-dom';

interface WhiteboardStore extends WhiteboardState {
  penSettings: PenSettings;
  recordingSession: RecordingSession;
  recordings: Recording[];
  currentStrokeId: string | null;
  selectedStrokeId: string | null;
  shapeRecognitionEnabled: boolean;
  showAnswerFeedback: boolean;
  showEliminationDisplay: boolean;

  glassEffects: boolean;
  strokeWidth: number;
  // Optical answers (includes '0' for visited but unanswered questions)
  opticalAnswers: Record<string, OpticalAnswer>;
  // Question data cache
  questionDataCache: Map<string, QuestionData>;
  pendingSaves: Map<string, QuestionData>;
  // Canvas reference
  canvasRef: FabricCanvas | null;
  // Current test ID for localStorage operations
  currentTestId: string | null;
  // Current question ID for optical choice tracking
  currentQuestionId: string | null;
  // Eliminated choices tracking
  eliminatedChoices: Record<string, string>;
  setCurrentTool: (tool: Tool) => void;
  setCurrentColor: (color: string) => void;
  setCurrentWidth: (width: number) => void;
  setIsDrawing: (isDrawing: boolean) => void;
  addPointToCurrentStroke: (point: Point) => void;
  setCurrentStrokeId: (id: string) => void;
  finishCurrentStroke: () => void;
  clearCurrentStroke: () => void;
  addStroke: (stroke: Stroke) => void;
  updateStroke: (strokeId: string, updatedStroke: Stroke) => void;
  removeStroke: (strokeId: string) => void;
  clearCanvas: () => void;
  clearStrokes: () => void;
  clearAllCanvasContent: () => void;
  clearAllDrawingHistory: () => void;
  cleanupSinglePointStrokes: () => void;
  undoLastStroke: () => void;
  // Undo/Redo system
  historyStack: any[];
  redoStack: any[];
  canUndo: boolean;
  canRedo: boolean;
  saveState: () => void;
  undo: () => void;
  redo: () => void;
  setCanvasSize: (size: { width: number; height: number }) => void;
  setPenMode: (isPenMode: boolean) => void;
  setTouchEnabled: (touchEnabled: boolean) => void;
  updatePenSettings: (settings: Partial<PenSettings>) => void;
  setHoveringCanvas: (hovering: boolean | null) => void;
  setPenOrStylus: (isPenOrStylus: boolean) => void;
  setCoarsePointer: (isCoarse: boolean) => void;
  deleteStrokesInArea: (strokeIds: string[]) => void;
  startRecording: (questionId?: string, testId?: string) => Promise<void>;
  stopRecording: () => Promise<void>;
  addRecordingEvent: (event: RecordingEvent) => void;
  saveRecording: (name: string, isPublic?: boolean, currentQuestionMetadata?: any) => void;
  deleteRecording: (id: string) => void;
  selectStroke: (strokeId: string | null) => void;
  moveSelectedStroke: (deltaX: number, deltaY: number) => void;
  setShapeRecognitionEnabled: (enabled: boolean) => void;
  recognizeLastStroke: () => void;
  setShowAnswerFeedback: (show: boolean) => void;
  setShowEliminationDisplay: (show: boolean) => void;
  setGlassEffects: (enabled: boolean) => void;
  // Question data cache methods
  setQuestionDataCache: (cache: Map<string, QuestionData>) => void;
  getQuestionData: (questionId: string) => QuestionData | undefined;
  updateQuestionDataCache: (questionId: string, data: QuestionData) => void;
  addPendingSave: (questionId: string, data: QuestionData) => void;
  getPendingSaves: () => Map<string, QuestionData>;
  clearPendingSaves: () => void;
  // Test metadata methods
  pendingTestMetadata: TestMetadata | null;
  setPendingTestMetadata: (metadata: TestMetadata | null) => void;
  // Device preferences
  devicePreferences: DevicePreferences | null;
  loadDevicePreferences: () => Promise<void>;
  saveDevicePreferences: () => Promise<void>;
  updateDevicePreferences: (updates: Partial<DevicePreferences['preferences']>) => Promise<void>;
  
  // Question timer preferences (NEW)
  showQuestionTimer: boolean;
  questionTimerAlerts: boolean;
  setShowQuestionTimer: (show: boolean) => void;
  setQuestionTimerAlerts: (enabled: boolean) => void;
  // Optical answers methods
  setOpticalAnswers: (answers: Record<string, OpticalAnswer>) => void;
  clearOpticalAnswers: () => void;
  // Eliminated choices methods
  setEliminatedChoices: (choices: Record<string, string>) => void;
  clearEliminatedChoices: () => void;
  // Canvas reference
  setCanvasRef: (canvas: FabricCanvas | null) => void;
  // Test ID management
  setCurrentTestId: (testId: string | null) => void;
  setCurrentQuestionId: (questionId: string | null) => void;
  getCurrentQuestionId: () => string | null;
  // Current question management
  currentQuestion: any | null;
  setCurrentQuestion: (question: any | null) => void;
  // Question positioning metadata
  questionPositionMetadata: Record<string, any>;
  setQuestionPositionMetadata: (questionId: string, metadata: any) => void;
  // Storage service
  getStorageService: () => any | null;
  setStorageService: (service: any) => void;
  storageService: any | null;
  setIsHoveringCanvas: (hovering: boolean) => void;
  // Recording state
  recording: any | null;
  isRecording: boolean;
  recordingEvents: any[];
  audioBlob: Blob | null;
}

// Tool-specific settings storage
export const toolSettings: Record<Tool, { color: string; width: number }> = {
  pen: { color: '#000000', width: 2 },
  pencil: { color: '#000000', width: 1 },
  eraser: { color: '#000000', width: 10 },
  highlighter: { color: '#FFFF00', width: 20 },
  laser: { color: '#FF0000', width: 3 },
  select: { color: '#000000', width: 1 },
  circle: { color: '#000000', width: 2 },
  rectangle: { color: '#000000', width: 2 },
  triangle: { color: '#000000', width: 2 },
  arrow: { color: '#000000', width: 2 },
  line: { color: '#000000', width: 2 },
  'area-eraser': { color: '#000000', width: 1 },
  'object-eraser': { color: '#000000', width: 20 },
  star: { color: '#000000', width: 2 },
  hexagon: { color: '#000000', width: 2 },
  pentagon: { color: '#000000', width: 2 },
  ellipse: { color: '#000000', width: 2 },
  cube: { color: '#000000', width: 2 },
  cylinder: { color: '#000000', width: 2 },
  pyramid: { color: '#000000', width: 2 },
  coordinate: { color: '#000000', width: 1 },
  grid: { color: '#000000', width: 1 },
  parallelogram: { color: '#000000', width: 2 },
  trapezoid: { color: '#000000', width: 2 },
  diamond: { color: '#000000', width: 2 },
};

export const useWhiteboardStore = create<WhiteboardStore>((set, get) => {
  // Default tool is explicitly set to 'pen'
  const DEFAULT_TOOL = 'pen';

  
  return {
  strokes: [],
  currentTool: DEFAULT_TOOL,
  currentColor: '#000000',
  currentWidth: 2,
  strokeWidth: 2,
  isDrawing: false,
  currentStrokeId: null,
  // Undo/Redo state
  historyStack: [],
  redoStack: [],
  canUndo: false,
  canRedo: false,
  currentStroke: [],
  canvasSize: { width: 0, height: 0 },
  isPenMode: false,
  touchEnabled: true,
  isHoveringCanvas: null,
  isPenOrStylus: false,
  isCoarsePointer: false,
  penSettings: {
    sensitivity: 1.0,
    smoothing: 0.5,
    pressureSensitivity: true,
    tiltSupport: true,
  },
  shapeRecognitionEnabled: false,
  showAnswerFeedback: true,
  showEliminationDisplay: true, // Default to true for desktop, mobile can override

  glassEffects: false,
  selectedStrokeId: null,
  recordingSession: {
    isRecording: false,
    startTime: 0,
    events: [],
    audioChunks: [],
  },
  recordings: [],
  // Question data cache
  questionDataCache: new Map(),
  pendingSaves: new Map(),
  pendingTestMetadata: null,
  
  // Storage service
  storageService: null,
  
  // Question timer preferences
  showQuestionTimer: false, // Default off except for TimedQuestion mode
  questionTimerAlerts: true, // Default on for warnings
  
  // Optical answers
  opticalAnswers: {},
  
  // Eliminated choices
  eliminatedChoices: {},
  
  // Canvas reference
  canvasRef: null,
  
  // Current test ID
  currentTestId: null,
  
  // Current question ID
  currentQuestionId: null,
  
  // Current question
  currentQuestion: null,
  
  // Question positioning metadata
  questionPositionMetadata: {},
  
  // Recording state
  recording: null,
  isRecording: false,
  recordingEvents: [],
  audioBlob: null,

  setCurrentTool: (tool) => {
  
    const currentTool = get().currentTool;
    const currentColor = get().currentColor;
    const currentWidth = get().currentWidth;
    
    // Save current tool's settings
    if (currentTool !== 'eraser') {
      toolSettings[currentTool].color = currentColor;
    }
    toolSettings[currentTool].width = currentWidth;
    
    // Load new tool's settings
    const newToolSettings = toolSettings[tool];
   
    set({ 
      currentTool: tool,
      currentColor: tool === 'eraser' ? currentColor : newToolSettings.color,
      currentWidth: newToolSettings.width,
      strokeWidth: newToolSettings.width // Also update strokeWidth
    });
   
    
    get().addRecordingEvent({
      type: 'tool_change',
      timestamp: 0,
      data: { tool }
    });
  },
  setCurrentColor: (color) => {
    set({ currentColor: color });
    get().addRecordingEvent({
      type: 'color_change',
      timestamp: 0,
      data: { color }
    });
  },
  setCurrentWidth: (width) => {
    set({ currentWidth: width, strokeWidth: width });
    get().addRecordingEvent({
      type: 'width_change',
      timestamp: 0,
      data: { width }
    });
  },
  setIsDrawing: (isDrawing) => set({ isDrawing }),
  
  addPointToCurrentStroke: (point) => {
    const { currentStroke } = get();
    set({ currentStroke: [...currentStroke, point] });
  },
  
  setCurrentStrokeId: (id: string) => {
    set({ currentStrokeId: id });
  },

  finishCurrentStroke: () => {
    const { currentStroke, currentTool, currentColor, currentWidth, strokes, addRecordingEvent, currentStrokeId, currentQuestionId } = get();
    if (currentStroke.length > 0) {
      const newStroke: Stroke = {
        id: currentStrokeId || `stroke-${Date.now()}-${Math.random()}`,
        points: [...currentStroke],
        color: currentColor,
        width: currentWidth,
        tool: currentTool,
        timestamp: Date.now(),
        questionId: currentQuestionId || undefined // Add questionId to stroke
      };
      
     
      set({ 
        strokes: [...strokes, newStroke],
        currentStroke: [],
        currentStrokeId: null
      });
      
      // Add the complete stroke event for backward compatibility and final stroke data
      // This will be used when progressive events are not available
      addRecordingEvent({
        type: 'add_stroke',
        timestamp: 0,
        data: { stroke: newStroke }
      });
      
      // Check for optical answer detection if in pen mode
      if (currentTool === 'pen' && currentQuestionId) {
        // This will be handled by the component that has access to optical choice positions
        // We just need to ensure the stroke is added to the store
      }
    }
  },

  clearCurrentStroke: () => {
    set({ currentStroke: [] });
  },

  addStroke: (stroke: Stroke) => {
    const { strokes, addRecordingEvent, currentQuestionId } = get();
    
    // Don't add single-point strokes EXCEPT for shapes (they start with 1 point)
    const isShape = [
      'rectangle', 'circle', 'line', 'triangle', 'arrow', 'star', 
      'hexagon', 'pentagon', 'ellipse', 'cube', 'cylinder', 'pyramid', 
      'coordinate', 'grid', 'parallelogram', 'trapezoid', 'diamond',
      'coordinateGrid'
    ].includes(stroke.tool);
    
    if (stroke.points.length <= 1 && !isShape) {
    
      return;
    }
    
    // Add questionId to stroke if not already set
    const strokeWithQuestionId = {
      ...stroke,
      questionId: stroke.questionId || currentQuestionId || undefined
    };
    
    set({
      strokes: [...strokes, strokeWithQuestionId],
      currentStroke: [],
      isDrawing: false
    });
    
    // Add recording event for the stroke
    addRecordingEvent({
      type: 'add_stroke',
      timestamp: 0,
      data: { stroke: strokeWithQuestionId }
    });
  },

  updateStroke: (strokeId: string, updatedStroke: Stroke) => {
    const { strokes } = get();
    const updatedStrokes = strokes.map(stroke => 
      stroke.id === strokeId ? updatedStroke : stroke
    );
    set({ strokes: updatedStrokes });
  },

  removeStroke: (strokeId: string) => {
    const { strokes, saveState } = get();
    
    // Save state before removing
    saveState();
    
    // Remove the stroke
    const newStrokes = strokes.filter(stroke => stroke.id !== strokeId);
    set({ strokes: newStrokes });
    
    // Record the deletion
    get().addRecordingEvent({
      type: 'stroke_delete',
      timestamp: 0,
      data: { strokeId }
    });
  },

  clearCanvas: () => {
    const { addRecordingEvent, saveState, canvasRef } = get();
    
    // Save current state before clearing for undo
    saveState();
    
    // Clear strokes from state
    set({ 
      strokes: [], 
      currentStroke: [], 
      isDrawing: false,
      selectedStrokeId: null 
    });
    
    // Clear canvas objects (except protected ones)
    if (canvasRef) {
      // Get all objects
      const allObjects = canvasRef.getObjects();
      const objectsToRemove: any[] = [];
      
      // Filter objects to remove (keep protected objects)
      allObjects.forEach((obj: any) => {
        const objName = obj.name || '';
        const objType = obj.type || '';
        
        // Skip protected question elements
        if (objName === 'questionBackground' || 
            objName === 'parentQuestionBackground' ||
            objName === 'questionFrame' ||
            objName === 'questionResizeHandle' ||
            objName === 'questionMoveHandle' ||
            objName === 'questionMoveIcon' ||
            objName?.startsWith('opticalChoice_') ||
            objName?.startsWith('opticalChoiceFeedback_') ||
            objName?.startsWith('opticalQuestionNumber_') ||
            objName?.startsWith('questionOpticalChoice_')) {
          return;
        }
        
        // Also check erasable property
        if (obj.erasable === false) {
          return;
        }
        
        // Only remove user drawings (marked with isUserDrawing or has strokeId)
        if ((obj.isUserDrawing === true || obj.strokeId) && 
            (objType === 'path' || objType === 'line' || objType === 'rect' || 
             objType === 'circle' || objType === 'ellipse' || objType === 'polygon' ||
             objType === 'group')) {
          objectsToRemove.push(obj);
        }
      });
      
      // Remove objects
      objectsToRemove.forEach((obj: any) => {
        canvasRef.remove(obj);
      });
      
      canvasRef.requestRenderAll();
    }
    
    addRecordingEvent({
      type: 'clear',
      timestamp: 0,
      data: {}
    });
  },

  clearStrokes: () => {
    // Clear only strokes without saving to history or adding recording event
    set({ 
      strokes: [], 
      currentStroke: [], 
      isDrawing: false,
      selectedStrokeId: null 
    });
  },

  clearAllDrawingHistory: () => {
    // Clear all drawing-related state and history
    set({
      strokes: [],
      currentStroke: [],
      isDrawing: false,
      selectedStrokeId: null,
      historyStack: [],
      redoStack: [],
      canUndo: false,
      canRedo: false,
      recordingEvents: [],
    });
  },

  clearAllCanvasContent: () => {
    const { canvasRef, clearStrokes, addRecordingEvent } = get();
    
    // Clear strokes from store
    clearStrokes();
    
    // Clear canvas if available
    if (canvasRef) {
      // Get all objects
      const allObjects = canvasRef.getObjects();
      const objectsToRemove: any[] = [];
      
      // Mark all user-drawn objects for removal
      allObjects.forEach((obj: any) => {
        const objType = obj.type;
        const objName = obj.name;
        
        // Skip non-removable objects
        if (objName === 'questionBackground' || 
            objName === 'questionImage' ||
            objName === 'questionFrame' ||
            objName === 'questionResizeHandle' ||
            objName === 'questionMoveHandle' ||
            objName === 'questionMoveIcon' ||
            objName?.startsWith('opticalChoice_') ||
            objName?.startsWith('opticalChoiceFeedback_') ||
            objName?.startsWith('opticalQuestionNumber_') ||
            objName?.startsWith('questionOpticalChoice_')) {
          return;
        }
        
        // Also check erasable property
        if (obj.erasable === false) {
          return;
        }
        
        // Add all user drawings to removal list
        if ((obj.isUserDrawing === true || obj.strokeId) && 
            (objType === 'path' || objType === 'line' || objType === 'rect' || 
             objType === 'circle' || objType === 'ellipse' || objType === 'polygon' ||
             objType === 'group')) {
          objectsToRemove.push(obj);
        }
      });
      
      // Remove objects
      objectsToRemove.forEach((obj: any) => {
        canvasRef.remove(obj);
      });
      
      canvasRef.requestRenderAll();
    }
    
    // Add recording event
    addRecordingEvent({
      type: 'clear',
      timestamp: 0,
      data: {}
    });
  },
  
  cleanupSinglePointStrokes: () => {
    const { strokes, currentQuestionId } = get();
    // Filter out only empty strokes (no points at all) for current question
    const cleanedStrokes = strokes.filter(stroke => {
      // Only clean empty strokes (no points) for current question
      if (stroke.questionId === currentQuestionId && stroke.points.length === 0) {
        return false;
      }
      return true;
    });
    
    if (cleanedStrokes.length !== strokes.length) {
      set({ strokes: cleanedStrokes });
    }
  },

  undoLastStroke: () => {
    const { strokes, addRecordingEvent } = get();
    if (strokes.length > 0) {
      set({ strokes: strokes.slice(0, -1) });
      addRecordingEvent({
        type: 'undo',
        timestamp: 0,
        data: {}
      });
    }
  },

  // Save current state to history
  saveState: () => {
    const { strokes, historyStack } = get();
    const newHistoryStack = [...historyStack, JSON.parse(JSON.stringify(strokes))];
    
    // Limit history size to prevent memory issues
    const maxHistorySize = 50;
    if (newHistoryStack.length > maxHistorySize) {
      newHistoryStack.shift();
    }

    set({ 
      historyStack: newHistoryStack,
      redoStack: [], // Clear redo stack when new action is performed
      canUndo: true,
      canRedo: false
    });
  },

  // Undo action
  undo: () => {
    const { strokes, historyStack, redoStack, addRecordingEvent } = get();
    
    if (historyStack.length === 0) return;

    // Save current state to redo stack
    const currentState = JSON.parse(JSON.stringify(strokes));
    const newRedoStack = [currentState, ...redoStack];
    
    // Get previous state from history
    const previousState = historyStack[historyStack.length - 1];
    const newHistoryStack = historyStack.slice(0, -1);

    // Find which strokes were removed (for undo, we need to remove strokes that were added)
    const currentStrokeIds = new Set(currentState.map((s: Stroke) => s.id));
    const previousStrokeIds = new Set(previousState.map((s: Stroke) => s.id));
    
    // Strokes to remove (were in current but not in previous)
    currentStrokeIds.forEach(id => {
      if (!previousStrokeIds.has(id)) {
        addRecordingEvent({
          type: 'stroke_delete',
          timestamp: 0,
          data: { strokeId: String(id) }
        });
      }
    });
    
    // Strokes to add back (were in previous but not in current) 
    previousState.forEach((stroke: Stroke) => {
      if (!currentStrokeIds.has(stroke.id)) {
        addRecordingEvent({
          type: 'add_stroke',
          timestamp: 0,
          data: { stroke }
        });
      }
    });

    set({
      strokes: JSON.parse(JSON.stringify(previousState)),
      historyStack: newHistoryStack,
      redoStack: newRedoStack,
      canUndo: newHistoryStack.length > 0,
      canRedo: true
    });

    addRecordingEvent({
      type: 'undo',
      timestamp: 0,
      data: {}
    });
  },

  // Redo action
  redo: () => {
    const { redoStack, historyStack, strokes, addRecordingEvent } = get();
    
    if (redoStack.length === 0) return;

    // Save current state to history
    const currentState = JSON.parse(JSON.stringify(strokes));
    const newHistoryStack = [...historyStack, currentState];
    
    // Get next state from redo stack
    const nextState = redoStack[0];
    const newRedoStack = redoStack.slice(1);

    // Find which strokes need to be deleted or added
    const currentStrokeIds = new Set(currentState.map((s: Stroke) => s.id));
    const nextStrokeIds = new Set(nextState.map((s: Stroke) => s.id));
    
    // Strokes to remove (were in current but not in next)
    currentStrokeIds.forEach(id => {
      if (!nextStrokeIds.has(id)) {
        addRecordingEvent({
          type: 'stroke_delete',
          timestamp: 0,
          data: { strokeId: String(id) }
        });
      }
    });
    
    // Strokes to add (were in next but not in current)
    nextState.forEach((stroke: Stroke) => {
      if (!currentStrokeIds.has(stroke.id)) {
        addRecordingEvent({
          type: 'add_stroke',
          timestamp: 0,
          data: { stroke }
        });
      }
    });

    set({
      strokes: JSON.parse(JSON.stringify(nextState)),
      historyStack: newHistoryStack,
      redoStack: newRedoStack,
      canUndo: true,
      canRedo: newRedoStack.length > 0
    });

    addRecordingEvent({
      type: 'redo',
      timestamp: 0,
      data: {}
    });
  },

  setCanvasSize: (size) => set({ canvasSize: size }),
  setPenMode: (isPenMode) => set({ isPenMode }),
  setTouchEnabled: (touchEnabled) => set({ touchEnabled }),
  setHoveringCanvas: (hovering) => set({ isHoveringCanvas: hovering }),
  setPenOrStylus: (isPenOrStylus) => set({ isPenOrStylus }),
  setCoarsePointer: (isCoarse) => set({ isCoarsePointer: isCoarse }),
  
  updatePenSettings: (settings) => {
    const { penSettings } = get();
    set({ penSettings: { ...penSettings, ...settings } });
  },

  deleteStrokesInArea: (strokeIds: string[]) => {
    const { strokes } = get();
    const newStrokes = strokes.filter(stroke => !strokeIds.includes(stroke.id));
    set({ strokes: newStrokes });
    
    // Record the deletion
    strokeIds.forEach(strokeId => {
      get().addRecordingEvent({
        type: 'stroke_delete',
        timestamp: 0,
        data: { strokeId }
      });
    });
  },

  // removeStroke: (strokeId: string) => {
  //   const { strokes, saveState } = get();
    
  //   // Save state before removing
  //   saveState();
    
  //   // Remove the stroke
  //   const newStrokes = strokes.filter(stroke => stroke.id !== strokeId);
  //   set({ strokes: newStrokes });
    
  //   // Record the deletion
  //   get().addRecordingEvent({
  //     type: 'stroke_delete',
  //     timestamp: 0,
  //     data: { strokeId }
  //   });
  // },

  setShapeRecognitionEnabled: (enabled: boolean) => set({ shapeRecognitionEnabled: enabled }),

  setShowAnswerFeedback: (show: boolean) => set({ showAnswerFeedback: show }),
  setShowEliminationDisplay: (show: boolean) => {
    set({ showEliminationDisplay: show });
    get().saveDevicePreferences();
  },

  setGlassEffects: (enabled: boolean) => set({ glassEffects: enabled }),

  recognizeLastStroke: () => {
    const { strokes } = get();
    if (strokes.length === 0) return;

    const lastStroke = strokes[strokes.length - 1];

    // Import şekil tanıma dinamik olarak
    import('../utils/shapeRecognition').then((module) => {
      const recognition = module.recognizeShape(lastStroke.points);

      if (recognition && recognition.type && recognition.confidence > 0.7) {
        // Keep original points, just mark as recognized shape
        const recognizedStroke: Stroke = {
          ...lastStroke,
          isRecognizedShape: true,
          originalShape: recognition.type,
          confidence: recognition.confidence
        };

        // Replace last stroke with recognized version
        const newStrokes = [...strokes.slice(0, -1), recognizedStroke];
        set({ strokes: newStrokes });
        
        // Record shape recognition
        get().addRecordingEvent({
          type: 'shape_recognized',
          timestamp: 0,
          data: { 
            strokeId: recognizedStroke.id, 
            shape: recognition.type, 
            confidence: recognition.confidence 
          }
        });
      }
    });
  },

  selectStroke: (strokeId: string | null) => {
    set({ selectedStrokeId: strokeId });
    
    // Record selection/deselection
    if (strokeId) {
      get().addRecordingEvent({
        type: 'stroke_select',
        timestamp: 0,
        data: { strokeId }
      });
    } else {
      get().addRecordingEvent({
        type: 'stroke_deselect',
        timestamp: 0,
        data: {}
      });
    }
  },

  moveSelectedStroke: (deltaX: number, deltaY: number) => {
    const { strokes, selectedStrokeId } = get();
    if (!selectedStrokeId) {
      return;
    }


    const newStrokes = strokes.map(stroke => {
      if (stroke.id === selectedStrokeId) {
        const movedStroke = {
          ...stroke,
          points: stroke.points.map(point => ({
            ...point,
            x: point.x + deltaX,
            y: point.y + deltaY
          }))
        };
        return movedStroke;
      }
      return stroke;
    });

    set({ strokes: newStrokes });
    
    // Record the move
    get().addRecordingEvent({
      type: 'stroke_move',
      timestamp: 0,
      data: { strokeId: selectedStrokeId, deltaX, deltaY }
    });
  },

  startRecording: async (questionId?: string, testId?: string) => {
    
    // Use fixed IDs for blank whiteboard
    const BLANK_WHITEBOARD_TEST_ID = 'blank-whiteboard-00000000-0000-0000-0000-000000000000';
    const BLANK_WHITEBOARD_QUESTION_ID = 'blank-question-00000000-0000-0000-0000-000000000000';
    
    const finalQuestionId = (!questionId || questionId === BLANK_WHITEBOARD_QUESTION_ID) 
      ? BLANK_WHITEBOARD_QUESTION_ID 
      : questionId;
    const finalTestId = (!testId || testId === BLANK_WHITEBOARD_TEST_ID) 
      ? BLANK_WHITEBOARD_TEST_ID 
      : testId;
    
    try {
      // Check current canvas size
      const currentCanvasSize = get().canvasSize;     
      
      // If canvas size is 0, try to get it from the window
      if (currentCanvasSize.width === 0 || currentCanvasSize.height === 0) {
        get().setCanvasSize({
          width: window.innerWidth || 1920,
          height: window.innerHeight || 1080
        });
      }
      
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Determine the best audio format for the current browser
      let mimeType = '';
      
      // Prioritize mp4 format as requested
      const possibleTypes = [
        'audio/mp4',
        'audio/aac',
        'audio/mpeg',
        'audio/webm;codecs=opus', // Fallback to webm with opus codec
        'audio/webm',
        'audio/ogg',
      ];
      
      // Find the first supported format
      for (const type of possibleTypes) {
        if (MediaRecorder.isTypeSupported(type)) {
          mimeType = type;
          break;
        }
      }
      
      // If no specific format is supported, let the browser choose
      
      const BIT_RATE = 64000; // 64 kbps for compact size (same as my-whiteboard)
      
      const mediaRecorderOptions: MediaRecorderOptions = {
        bitsPerSecond: BIT_RATE,
        ...(mimeType && { mimeType })
      };
      
      const mediaRecorder = new MediaRecorder(stream, mediaRecorderOptions);
      const audioChunks: Blob[] = [];
      
      // Store the mime type for later use
      const recordingMimeType = mimeType;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunks.push(event.data);
        }
      };

      mediaRecorder.onerror = (event) => {
        console.error('❌ MediaRecorder error:', event);
      };

      mediaRecorder.start(1000); // Capture data every second

      const startTime = Date.now();
      
      // Use final IDs for recording
      const recordingTestId = finalTestId;
      const recordingQuestionId = finalQuestionId;
      
      
      // Record initial state
      const initialEvents = [
        { type: 'tool_change' as const, timestamp: 0, data: { tool: get().currentTool } },
        { type: 'color_change' as const, timestamp: 0, data: { color: get().currentColor } },
        { type: 'width_change' as const, timestamp: 0, data: { width: get().currentWidth } }
      ];
      
      set({
        recordingSession: {
          isRecording: true,
          startTime,
          events: initialEvents,
          mediaRecorder,
          audioChunks,
          questionId,
          testId: recordingTestId,
          mimeType: recordingMimeType
        }
      });
      
    } catch (error) {
      console.error('❌ Recording failed:', error);
      
      // Show user-friendly error message
      if (error instanceof DOMException) {
        if (error.name === 'NotAllowedError') {
          alert('Mikrofon izni reddedildi. Lütfen tarayıcı ayarlarından mikrofon iznini etkinleştirin.');
        } else if (error.name === 'NotFoundError') {
          alert('Mikrofon bulunamadı. Lütfen cihazınıza bir mikrofon bağlayın.');
        } else if (error.name === 'NotReadableError') {
          alert('Mikrofon başka bir uygulama tarafından kullanılıyor olabilir.');
        } else {
          alert(`Kayıt başlatılamadı: ${error.message}`);
        }
      } else {
        alert('Kayıt başlatılırken bir hata oluştu. Lütfen tekrar deneyin.');
      }
      
      // Reset recording state
      set({
        recordingSession: {
          isRecording: false,
          startTime: 0,
          events: [],
          mediaRecorder: undefined,
          audioChunks: [],
          questionId: undefined,
          testId: undefined
        }
      });
    }
  },

  stopRecording: async () => {
    const { recordingSession } = get();
    if (!recordingSession.isRecording || !recordingSession.mediaRecorder) return;

    return new Promise<void>((resolve) => {
      recordingSession.mediaRecorder!.onstop = () => {
        set({
          recordingSession: {
            ...recordingSession,
            isRecording: false,
          }
        });
        resolve();
      };

      recordingSession.mediaRecorder!.stop();
      recordingSession.mediaRecorder!.stream.getTracks().forEach(track => track.stop());
    });
  },

  addRecordingEvent: (event) => {
    const { recordingSession } = get();
    if (!recordingSession.isRecording) return;

    // Deep copy the event to ensure all nested properties are preserved
    const eventWithTimestamp = {
      type: event.type,
      timestamp: Date.now() - recordingSession.startTime,
      data: event.data ? { ...event.data } : event.data
    };


    // For high-frequency events like stroke_add, we need to ensure they're not lost
    // Use get() to get the latest state instead of relying on closure
    set((state) => {
      const newEvents = [...state.recordingSession.events, eventWithTimestamp as any];
      return {
        recordingSession: {
          ...state.recordingSession,
          events: newEvents,
        }
      };
    });
  },

  saveRecording: async (name, isPublic = false, currentQuestionMetadata?: any) => {
      
    const { recordingSession, recordings, canvasSize } = get();
    

    
    // Ensure canvas size is valid
    const validCanvasSize = {
      width: canvasSize.width > 0 ? canvasSize.width : (window.innerWidth || 1920),
      height: canvasSize.height > 0 ? canvasSize.height : (window.innerHeight || 1080)
    };
    
    if (canvasSize.width === 0 || canvasSize.height === 0) {
    }

    if (!recordingSession.events.length) {
      console.error('❌ No recording events to save');
      return;
    }

    // Use the mime type that was used during recording (default to audio/mp4)
    const mimeType = recordingSession.mimeType || 'audio/mp4';
    let audioBlob = recordingSession.audioChunks.length > 0
      ? new Blob(recordingSession.audioChunks, { type: mimeType })
      : new Blob([], { type: mimeType }); // Empty blob if no audio
    
    
    // Fix WebM duration if needed
    if (mimeType.includes('webm') && audioBlob.size > 0) {
      try {
        audioBlob = await fixWebmDuration(audioBlob);
        
        // Validate the audio
        const url = URL.createObjectURL(audioBlob);
        const audio = new Audio();
        
        await new Promise<void>((resolve, reject) => {
          audio.onloadedmetadata = () => {
            URL.revokeObjectURL(url);
            resolve();
          };
          
          audio.onerror = () => {
            console.error('❌ Audio validation failed:', audio.error);
            URL.revokeObjectURL(url);
            reject(new Error('Audio validation failed'));
          };
          
          audio.src = url;
        });
      } catch (fixError) {
        // Continue with the original blob
      }
    }

   

    // Get user and tenant info
    const userInfo = useUserStore.getState().userInfo;
    const tenantStore = useTenantStore.getState();    
  
    
    // Get test ID from store (set by Whiteboard component)
    const storeTestId = get().currentTestId;
    
    // Get current question from store
    const currentQuestion = get().currentQuestion;
    
   
    
    // Generate proper recording ID: tenantId-testId-userId-questionId-solutionNumber-timestamp
    const tenantId = tenantStore.tenantId || 0;
    const userId = typeof userInfo?.id === 'string' ? userInfo.id : (userInfo?.id || 0).toString();
    // Use fixed IDs for blank whiteboard
    const BLANK_WHITEBOARD_TEST_ID = 'blank-whiteboard-00000000-0000-0000-0000-000000000000';
    const BLANK_WHITEBOARD_QUESTION_ID = 'blank-question-00000000-0000-0000-0000-000000000000';
    
    const testIdForRecording = (recordingSession.testId === BLANK_WHITEBOARD_TEST_ID || !recordingSession.testId)
      ? BLANK_WHITEBOARD_TEST_ID
      : (recordingSession.testId || storeTestId || 'unknown');
      
    const questionId = (recordingSession.questionId === BLANK_WHITEBOARD_QUESTION_ID || !recordingSession.questionId)
      ? BLANK_WHITEBOARD_QUESTION_ID
      : (recordingSession.questionId || currentQuestion?.questionId || currentQuestion?.id || 'general');
    const timestamp = Date.now();
    
   
    
    // Get next solution number
    let solutionNumber = 1;
    try {
      solutionNumber = await RecordingService.getNextSolutionNumber(
        testIdForRecording,
        questionId,
        tenantId,
        userId
      );
    } catch (error) {
      console.error('Error getting solution number, defaulting to 1:', error);
    }
    
   
    
    const recordingId = `${tenantId}-${testIdForRecording}-${userId}-${questionId}-${solutionNumber}-${timestamp}`;
    
    // Get current question URL and metadata
    const currentUrl = window.location.href;
    
    // Use provided metadata or empty object
    const questionMetadata = currentQuestionMetadata || {};
    
    const recording: Recording = {
      id: recordingId,
      name,
      duration: Date.now() - recordingSession.startTime,
      audioBlob,
      events: recordingSession.events,
      createdAt: Date.now(),
      canvasSize: validCanvasSize,
      questionId: recordingSession.questionId,
      testId: recordingSession.testId,
      questionImageUrl: currentQuestion?.imageUrl,
      parentImageUrl: currentQuestion?.parentQuestionImageUrl,
      questionMetadata: questionMetadata // Add the question position/scale metadata
    };

    try {
      // Delete old recordings for same tenant/user/question
      if (tenantId && userId && questionId && questionId !== 'general') {
        try {
          await RecordingService.deleteRecordingsByQuestion(tenantId, userId, questionId);
        } catch (deleteError) {
        }
      }

      // Save to Firebase with enhanced metadata
    
      
      const metadata: Parameters<typeof RecordingService.saveRecording>[3] = {
        authorMetadata: {
          id: typeof userId === 'string' ? (parseInt(userId) || userId) : (userId || 0),
          name: userInfo?.fullName && userInfo.fullName !== 'Anonymous' 
            ? userInfo.fullName 
            : (userInfo?.name && userInfo?.surname 
              ? `${userInfo.name} ${userInfo.surname}`.trim() 
              : userInfo?.userName || 'Anonymous'),
          isAnonymous: !userInfo
        },
        whiteboardSize: validCanvasSize,
        tenantMetadata: {
          id: tenantId
        },
        solutionMetadata: {
          solutionNumber,
          solutionName: name || `Çözüm ${solutionNumber}`,
          isPublic: false, // Default to private
          allowDownload: false // Default to no download
        },
        ...((questionId && questionId !== 'general') ? {
          questionMetadata: {
            id: questionId,
            title: currentQuestion?.title || currentQuestion?.questionTitle || name,
            testId: testIdForRecording,
            testTitle: name,
            url: currentUrl,
            timestamp: timestamp,
            ...(currentQuestion?.imageUrl && { imageUrl: currentQuestion.imageUrl }),
            ...(currentQuestion?.parentQuestionImageUrl && { parentImageUrl: currentQuestion.parentQuestionImageUrl }),
            ...(currentQuestion?.orderNo !== undefined && { questionNo: currentQuestion.orderNo }),
            ...(currentQuestion?.questionOrder !== undefined && { questionNo: currentQuestion.questionOrder }),
            // Include position data directly in questionMetadata
            ...(currentQuestionMetadata && { 
              position: {
                scale: currentQuestionMetadata.scale,
                left: currentQuestionMetadata.left,
                top: currentQuestionMetadata.top
              }
            })
          }
        } : {})
      }

     
      
      // Get current strokes to update the events with final stroke data
      const currentStrokes = get().strokes;
      
      // Track which strokes have progressive events
      const strokesWithProgressiveEvents = new Set<string>();
      recordingSession.events.forEach(event => {
        if ((event.type === 'stroke_start' || event.type === 'stroke_end') && event.data.strokeId) {
          strokesWithProgressiveEvents.add(event.data.strokeId);
        }
      });
      
      
      // Filter and normalize recording events for cross-device compatibility
      // Keep all events - we want both progressive and add_stroke events
      // The player will handle which ones to use
      const filteredEvents = recordingSession.events;
      
      // Log event type statistics
      const eventStats: Record<string, number> = {};
      filteredEvents.forEach(event => {
        eventStats[event.type] = (eventStats[event.type] || 0) + 1;
      });
      
      
      const normalizedEvents = filteredEvents.map(event => {
        // For add_stroke events, update with the current stroke data from the store
        if (event.type === 'add_stroke' && 'stroke' in event.data && event.data.stroke) {
          const strokeData = event.data as { stroke: Stroke };
          const currentStroke = currentStrokes.find(s => s.id === strokeData.stroke.id);
          if (currentStroke) {
           
            event = {
              ...event,
              data: {
                ...event.data,
                stroke: currentStroke
              }
            };
          }
        }
        
        const normalized = normalizeRecordingEvent(event, validCanvasSize.width, validCanvasSize.height);
        // Mark events as normalized for playback
        if (normalized.type === 'add_stroke' && normalized.data.stroke) {
          (normalized.data.stroke as any)._isNormalized = true;
        }
        return normalized;
      });
      
    
      
      // Save recording with retry mechanism
      let saveAttempts = 0;
      const maxRetries = 3;
      let lastError: Error | null = null;
      
      while (saveAttempts < maxRetries) {
        saveAttempts++;
               
        try {
          await RecordingService.saveRecording(
            recordingId,
            normalizedEvents,
            audioBlob,
            metadata
          );
        
          break; // Success, exit retry loop
        } catch (error) {
          lastError = error as Error; 
          if (saveAttempts < maxRetries) {
            // Wait before retry with exponential backoff
            const waitTime = Math.min(1000 * Math.pow(2, saveAttempts - 1), 5000);
          
            await new Promise(resolve => setTimeout(resolve, waitTime));
          }
        }
      }
      
      if (lastError && saveAttempts === maxRetries) {
     
        throw lastError;
      }
      
    

    
      
      let analyticsAttempts = 0;
      const maxAnalyticsRetries = 3;
      let analyticsSuccess = false;
      
      while (analyticsAttempts < maxAnalyticsRetries && !analyticsSuccess) {
        analyticsAttempts++;
        
        try {
          const { RecordingAnalyticsService } = await import('../lib/firebase/recordingAnalyticsService');
          const currentDate = new Date(timestamp);
          
          await RecordingAnalyticsService.saveRecordingMetadata({
            id: recordingId,
            name,
            userId: parseInt(userId) || 0,
            userName: userInfo?.fullName && userInfo.fullName !== 'Anonymous' 
              ? userInfo.fullName 
              : (userInfo?.name && userInfo?.surname 
                ? `${userInfo.name} ${userInfo.surname}`.trim() 
                : userInfo?.userName || 'Anonymous'),
            tenantId,
            tenantName: tenantStore.formattedCode || 'Unknown',
            testId: testIdForRecording,
            testName: name,
            questionId: recordingSession.questionId || 'general',
            questionOrder: 0, // This should come from question data
            questionSubject: 'Unknown', // This should come from question data
            questionTopic: 'Unknown', // This should come from question data
            createdAt: timestamp,
            year: currentDate.getFullYear(),
            month: currentDate.getMonth() + 1,
            day: currentDate.getDate(),
            duration: timestamp - recordingSession.startTime,
            storagePath: `recordings/${tenantId}/${currentDate.getFullYear()}/${String(currentDate.getMonth() + 1).padStart(2, '0')}/${testIdForRecording}/${questionId}/${recordingId}`,
            hasAudio: audioBlob.size > 0,
            eventsCount: normalizedEvents.length,
            isCompleted: true,
            hasCorrectAnswer: false, // This should be determined from question data
            tools: [...new Set(recordingSession.events.filter(e => e.type === 'tool_change').map(e => e.data.tool))],
            searchTags: [name.toLowerCase(), testIdForRecording?.toLowerCase() || '', questionId?.toLowerCase() || ''].filter(Boolean)
          });
          
          analyticsSuccess = true;
         
        } catch (analyticsError) {
          console.error(`❌ Analytics save attempt ${analyticsAttempts} failed:`, analyticsError);
          
          if (analyticsAttempts < maxAnalyticsRetries) {
            const waitTime = Math.min(1000 * analyticsAttempts, 3000);
         
            await new Promise(resolve => setTimeout(resolve, waitTime));
          } else {
            // If analytics fails after all retries, log but don't fail the whole operation
            console.error('⚠️ Recording analytics save failed after all retries, but main recording is saved');
          }
        }
      }
      
   
      
      // Verify the recording was saved by trying to read it back
      
      
    } catch (error) {
      console.error('❌ Failed to save recording to Firebase:', error);
      console.error('Error details:', {
        name: error instanceof Error ? error.name : 'Unknown',
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined
      });
      
      // Check if it's a permission error
      if (error instanceof Error && (error.message.includes('permission') || error.message.includes('unauthorized'))) {
        console.error('🔒 Firebase permission error - check Firestore/Storage rules');
      }
      
      throw error; // Don't fall back to localStorage - show error to user
    }

    // Only keep recordings in memory for UI, no localStorage backup
    const updatedRecordings = [...recordings, recording];
    set({ recordings: updatedRecordings });
  },

  deleteRecording: (id) => {
    const { recordings } = get();
    const updatedRecordings = recordings.filter(r => r.id !== id);
    
    // Only update in-memory state, Firebase deletion handled separately
    set({ recordings: updatedRecordings });
  },

  // Question data cache methods
  setQuestionDataCache: (cache) => {
    // Ensure cache is always a Map
    if (!(cache instanceof Map)) {
      console.error('Attempting to set non-Map questionDataCache:', cache);
      set({ questionDataCache: new Map() });
      return;
    }
    set({ questionDataCache: cache });
  },

  getQuestionData: (questionId) => {
    const { questionDataCache, currentTestId } = get();
    // Ensure questionDataCache is a Map
    if (!(questionDataCache instanceof Map)) {
      console.error('questionDataCache is not a Map:', questionDataCache);
      return undefined;
    }
    
    // First check memory cache
    const cachedData = questionDataCache.get(questionId);
    if (cachedData) {
      return cachedData;
    }
    
    // Then check localStorage
    const userState = useUserStore.getState();
    const tenantState = useTenantStore.getState();
    
    if (userState.userInfo?.id && tenantState.tenantId && currentTestId) {
      // Try to load from localStorage
      const storedData = QuestionDataLocalStorage.getQuestionData(
        tenantState.tenantId,
        userState.userInfo.id,
        currentTestId,
        questionId
      );
      
      if (storedData) {
      
        // Add to memory cache for faster access
        const newCache = new Map(questionDataCache);
        newCache.set(questionId, storedData);
        set({ questionDataCache: newCache });
        return storedData;
      }
    }
    
    return undefined;
  },

  updateQuestionDataCache: (questionId, data) => {
    const { questionDataCache } = get();
    
    // Validate inputs
    if (!questionId || !data) {
      console.error('Invalid inputs to updateQuestionDataCache:', { questionId, data });
      return;
    }
    
    // Validate required fields
    if (!data.userId || !data.tenantId || !data.testId || !data.questionId) {
      console.error('Missing required fields in question data:', data);
      return;
    }
    
    // Save to localStorage immediately
    QuestionDataLocalStorage.saveQuestionData(data);
    
    // Ensure questionDataCache is a Map
    if (!(questionDataCache instanceof Map)) {
      console.error('questionDataCache is not a Map, creating new Map:', questionDataCache);
      const newCache = new Map();
      newCache.set(questionId, data);
      set({ questionDataCache: newCache });
      return;
    }
    const newCache = new Map(questionDataCache);
    newCache.set(questionId, data);
    set({ questionDataCache: newCache });
  },

  addPendingSave: (questionId, data) => {
    const { pendingSaves } = get();
    const newPendingSaves = new Map(pendingSaves);
    newPendingSaves.set(questionId, data);
    set({ pendingSaves: newPendingSaves });
  },

  getPendingSaves: () => {
    return get().pendingSaves;
  },

  clearPendingSaves: () => {
    set({ pendingSaves: new Map() });
  },

  setPendingTestMetadata: (metadata) => {
    set({ pendingTestMetadata: metadata });
  },

  // Device preferences
  devicePreferences: null,

  loadDevicePreferences: async () => {
    try {
    
      const userState = useUserStore.getState();
      const tenantState = useTenantStore.getState();
      
      if (!userState.userInfo?.id || !tenantState.tenantId) {
       
        // Set default preferences without Firebase
        const defaultPrefs = {
          penMode: false,
          touchEnabled: true,
          penSettings: {
            sensitivity: 0.5,
            smoothing: 0.5,
            pressureSensitivity: true,
            tiltSupport: false
          },
          defaultTool: 'pen' as Tool,
          defaultColor: '#000000',
          defaultWidth: 2
        };
        
       
        set({
          isPenMode: defaultPrefs.penMode,
          touchEnabled: defaultPrefs.touchEnabled,
          penSettings: defaultPrefs.penSettings,
          currentTool: defaultPrefs.defaultTool,
          currentColor: defaultPrefs.defaultColor,
          currentWidth: defaultPrefs.defaultWidth
        });
        return;
      }

      // Set user context for the service
      devicePreferencesService.setUserContext(tenantState.tenantId, userState.userInfo.id);
      
      // Load preferences
      const preferences = await devicePreferencesService.loadPreferences();
      if (preferences) {
        set({ devicePreferences: preferences });
        
        // Apply loaded preferences to current state
        const { preferences: prefs } = preferences;
               
        // Load tool-specific settings if available
        if (prefs.toolSettings) {
          Object.assign(toolSettings, prefs.toolSettings);
        }
        
        set({
          isPenMode: prefs.penMode,
          touchEnabled: prefs.touchEnabled,
          penSettings: prefs.penSettings,
          currentTool: (prefs.defaultTool as Tool) || 'pen',
          currentColor: prefs.defaultColor || '#000000',
          currentWidth: prefs.defaultWidth || 4,
          showAnswerFeedback: prefs.showAnswerFeedback !== undefined ? prefs.showAnswerFeedback : true,
          showEliminationDisplay: prefs.showEliminationDisplay !== undefined ? prefs.showEliminationDisplay : true,
          glassEffects: prefs.glassEffects !== undefined ? prefs.glassEffects : false
        });
       
      }
    } catch (error) {
      console.error('Error loading device preferences:', error);
    }
  },

  saveDevicePreferences: async () => {
    try {
      const state = get();
      const userState = useUserStore.getState();
      const tenantState = useTenantStore.getState();
      
      if (!userState.userInfo?.id || !tenantState.tenantId) {
        return;
      }

      const updates: Partial<DevicePreferences['preferences']> = {
        penMode: state.isPenMode,
        touchEnabled: state.touchEnabled,
        penSettings: state.penSettings,
        defaultTool: state.currentTool,
        defaultColor: state.currentColor,
        defaultWidth: state.currentWidth,
        showAnswerFeedback: state.showAnswerFeedback,
        showEliminationDisplay: state.showEliminationDisplay,
        glassEffects: state.glassEffects,
        toolSettings: toolSettings // Save all tool-specific settings
      };

      await devicePreferencesService.updatePreferences(updates);
    } catch (error) {
      console.error('Error saving device preferences:', error);
    }
  },

  updateDevicePreferences: async (updates) => {
    try {
      const success = await devicePreferencesService.updatePreferences(updates);
      if (success) {
        // Reload preferences to update local state
        await get().loadDevicePreferences();
      }
    } catch (error) {
      console.error('Error updating device preferences:', error);
    }
  },
  
  // Question timer preferences setters (NEW)
  setShowQuestionTimer: (show) => {
    set({ showQuestionTimer: show });
    // Save to localStorage for persistence
    localStorage.setItem('showQuestionTimer', JSON.stringify(show));
  },
  
  setQuestionTimerAlerts: (enabled) => {
    set({ questionTimerAlerts: enabled });
    // Save to localStorage for persistence
    localStorage.setItem('questionTimerAlerts', JSON.stringify(enabled));
  },
  
  setOpticalAnswers: (answers) => {
    const currentAnswers = get().opticalAnswers;
   
    set({ opticalAnswers: answers });
  },
  
  clearOpticalAnswers: () => {
   
    set({ opticalAnswers: {} });
  },
  
  setEliminatedChoices: (choices) => {
  
    set({ eliminatedChoices: choices });
  },
  
  clearEliminatedChoices: () => {
  
    set({ eliminatedChoices: {} });
  },
  
  setCanvasRef: (canvas) => {
    set({ canvasRef: canvas });
  },
  
  setCurrentTestId: (testId) => {
    set({ currentTestId: testId });
  },
  
  setCurrentQuestionId: (questionId) => {
    const previousId = get().currentQuestionId;

    set({ currentQuestionId: questionId });
    
    // DO NOT filter strokes when question changes - we need to keep all strokes
    // The canvas will handle showing only the strokes for the current question
    if (previousId !== questionId && questionId) {
      const { strokes } = get();
      const currentQuestionStrokes = strokes.filter(stroke => stroke.questionId === questionId);
    
    }
  },
  
  getCurrentQuestionId: () => {
    return get().currentQuestionId;
  },
  
  setCurrentQuestion: (question) => {
    set({ currentQuestion: question });
    // Also update currentQuestionId when question changes
    if (question?.questionId) {
      set({ currentQuestionId: question.questionId });
    }
  },
  
  getStorageService: () => {
    return get().storageService;
  },
  
  setStorageService: (service) => {
    set({ storageService: service });
  },
  
  setIsHoveringCanvas: (hovering) => {
    set({ isHoveringCanvas: hovering });
  },
  
  setQuestionPositionMetadata: (questionId, metadata) => {
    set(state => ({
      questionPositionMetadata: {
        ...state.questionPositionMetadata,
        [questionId]: metadata
      }
    }));
  },
};
});

// Initialize timer preferences from localStorage
if (typeof window !== 'undefined') {
  const savedShowTimer = localStorage.getItem('showQuestionTimer');
  const savedAlerts = localStorage.getItem('questionTimerAlerts');
  
  if (savedShowTimer !== null) {
    useWhiteboardStore.setState({ showQuestionTimer: JSON.parse(savedShowTimer) });
  }
  if (savedAlerts !== null) {
    useWhiteboardStore.setState({ questionTimerAlerts: JSON.parse(savedAlerts) });
  }
}